import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { getRegistrySites } from '../../api/assetRegistryApi';
import { getEligiblePool, createScrapProject } from '../../api/disposalApi';
import { useReferenceData } from '../../shared/hooks/useReferenceData';
import './CreateScrapProjectPage.css';

export default function CreateScrapProjectPage() {
  const { callerEmail } = useUser();
  const navigate = useNavigate();
  const { options: refOpts } = useReferenceData(['oem'], callerEmail);

  const [step, setStep] = useState(1);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  // Step 1 — form
  const [form, setForm] = useState({
    project_number: '', oem: '', site_id: '',
    supplier_name: '', site_name: '', site_city: '', site_state: '',
    hauler_name: '', estimated_weight_lbs: '', price_per_ton: '', notes: '',
  });
  const [errors, setErrors] = useState({});

  // Step 2 — tools
  const [eligibleTools, setEligibleTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Load sites on mount
  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getRegistrySites(callerEmail)
      .then(data => setSites(Array.isArray(data) ? data : data.sites || []))
      .catch(() => {})
      .finally(() => setSitesLoading(false));
  }, [callerEmail]);

  // OEM options from ReferenceData
  const oemOptions = refOpts.oem || [];

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }

  function handleSiteChange(siteId) {
    const site = sites.find(s => s.site_id === siteId);
    setForm(prev => ({
      ...prev,
      site_id: siteId,
      site_name: site?.site_name || '',
      site_city: site?.site_city || '',
      site_state: site?.site_state_province || '',
      supplier_name: site?.supplier_name || '',
    }));
  }

  function siteLabel(s) {
    if (s.site_city) return `${s.site_name} (${s.site_city})`;
    if (s.supplier_site_code) return `${s.site_name} (${s.supplier_site_code})`;
    return s.site_name || s.site_id;
  }

  // Step 1 → Step 2 validation
  function handleNext() {
    const errs = {};
    if (!form.project_number.trim()) errs.project_number = 'Project number is required';
    if (!form.oem) errs.oem = 'OEM is required';
    if (!form.site_id) errs.site_id = 'Site is required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Load eligible pool
    setStep(2);
    setToolsLoading(true);
    setSelected(new Set());
    setCreateError(null);
    getEligiblePool(form.site_id, callerEmail)
      .then(data => setEligibleTools(data.results || []))
      .catch(() => setEligibleTools([]))
      .finally(() => setToolsLoading(false));
  }

  // Tool selection
  function toggleTool(toolId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId); else next.add(toolId);
      return next;
    });
  }
  function toggleAll() {
    if (selected.size === eligibleTools.length) setSelected(new Set());
    else setSelected(new Set(eligibleTools.map(t => t.id || t.tool_id)));
  }

  const totalWeight = useMemo(() => {
    let w = 0;
    for (const t of eligibleTools) {
      if (selected.has(t.id || t.tool_id)) w += parseFloat(t.estimated_weight) || 0;
    }
    return w;
  }, [eligibleTools, selected]);

  // Create project
  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    try {
      const payload = {
        project_number: form.project_number.trim(),
        oem: form.oem,
        site_id: form.site_id,
        supplier_name: form.supplier_name,
        site_name: form.site_name,
        site_city: form.site_city,
        site_state: form.site_state,
        hauler_name: form.hauler_name || null,
        estimated_weight_lbs: form.estimated_weight_lbs ? parseFloat(form.estimated_weight_lbs) : null,
        price_per_ton: form.price_per_ton ? parseFloat(form.price_per_ton) : null,
        notes: form.notes || null,
        tool_ids: [...selected],
      };
      const result = await createScrapProject(payload, callerEmail);
      navigate(`/admin/disposal/${result.project_id}`, {
        state: { site_id: form.site_id },
      });
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  function handleCancel() {
    if (window.confirm('Discard this project?')) navigate('/admin/disposal');
  }

  return (
    <div className="csp-page">
      {/* Header */}
      <div className="csp-header">
        <button className="csp-back" onClick={() => navigate('/admin/disposal')}>
          &larr; Disposal Projects
        </button>
        <h1 className="csp-title">New Scrap Project</h1>
        <span className="csp-step-pill">Step {step} of 2</span>
      </div>

      <div className="csp-content">
        {/* ==================== STEP 1 ==================== */}
        {step === 1 && (
          <div className="csp-card">
            <div className="csp-form-grid">
              {/* Project Number */}
              <div className="csp-field csp-form-full">
                <label className="csp-label csp-label-required">Project Number</label>
                <input className={`csp-input ${errors.project_number ? 'csp-input-error' : ''}`}
                  value={form.project_number}
                  onChange={e => set('project_number', e.target.value)}
                  placeholder="e.g. SP-2026-001" />
                {errors.project_number && <span className="csp-error-text">{errors.project_number}</span>}
                <div className="csp-advisory">Verify project number format with your OEM before saving.</div>
              </div>

              {/* OEM */}
              <div className="csp-field">
                <label className="csp-label csp-label-required">OEM</label>
                <select className={`csp-input ${errors.oem ? 'csp-input-error' : ''}`}
                  value={form.oem} onChange={e => {
                    const val = e.target.value;
                    setForm(prev => ({
                      ...prev, oem: val,
                      site_id: '', supplier_name: '', site_name: '', site_city: '', site_state: '',
                    }));
                    setErrors(prev => ({ ...prev, oem: null, site_id: null }));
                  }}>
                  <option value="">Select OEM…</option>
                  {oemOptions.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                {errors.oem && <span className="csp-error-text">{errors.oem}</span>}
              </div>

              {/* Site */}
              <div className="csp-field">
                <label className="csp-label csp-label-required">Site</label>
                <select className={`csp-input ${errors.site_id ? 'csp-input-error' : ''}`}
                  value={form.site_id} onChange={e => handleSiteChange(e.target.value)}
                  disabled={sitesLoading}>
                  <option value="">Select site…</option>
                  {sites.map(s => <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>)}
                </select>
                {errors.site_id && <span className="csp-error-text">{errors.site_id}</span>}
              </div>

              {/* Auto-filled fields */}
              <div className="csp-field">
                <label className="csp-label">Supplier Company</label>
                <input className="csp-input" value={form.supplier_name}
                  onChange={e => set('supplier_name', e.target.value)} placeholder="Supplier name" />
              </div>
              <div className="csp-field">
                <label className="csp-label">Site Name</label>
                <input className="csp-input" value={form.site_name}
                  onChange={e => set('site_name', e.target.value)} placeholder="Site name" />
              </div>
              <div className="csp-field">
                <label className="csp-label">Supplier City</label>
                <input className="csp-input" value={form.site_city}
                  onChange={e => set('site_city', e.target.value)} placeholder="City" />
              </div>
              <div className="csp-field">
                <label className="csp-label">Supplier State</label>
                <input className="csp-input" value={form.site_state}
                  onChange={e => set('site_state', e.target.value)} placeholder="State/Province" />
              </div>

              {/* Optional fields */}
              <div className="csp-field">
                <label className="csp-label">Hauler Name</label>
                <input className="csp-input" value={form.hauler_name}
                  onChange={e => set('hauler_name', e.target.value)} placeholder="Hauler company" />
              </div>
              <div className="csp-field">
                <label className="csp-label">Estimated Weight (lbs)</label>
                <input className="csp-input" type="number" value={form.estimated_weight_lbs}
                  onChange={e => set('estimated_weight_lbs', e.target.value)} placeholder="0" />
              </div>
              <div className="csp-field">
                <label className="csp-label">Price Per Ton ($)</label>
                <input className="csp-input" type="number" step="0.01" value={form.price_per_ton}
                  onChange={e => set('price_per_ton', e.target.value)} placeholder="0.00" />
              </div>

              <div className="csp-field csp-form-full">
                <label className="csp-label">Notes</label>
                <textarea className="csp-input csp-textarea" value={form.notes}
                  onChange={e => set('notes', e.target.value)} placeholder="Optional project notes…" />
              </div>
            </div>

            <div className="csp-footer">
              <button className="csp-btn-cancel" onClick={handleCancel}>Cancel</button>
              <button className="csp-btn-primary" onClick={handleNext}>Next &rarr;</button>
            </div>
          </div>
        )}

        {/* ==================== STEP 2 ==================== */}
        {step === 2 && (
          <div className="csp-card">
            {/* Summary bar */}
            <div className="csp-summary-bar">
              <span><span className="csp-summary-label">Project:</span> {form.project_number}</span>
              <span><span className="csp-summary-label">OEM:</span> {form.oem}</span>
              <span><span className="csp-summary-label">Site:</span> {form.site_name || form.site_id}</span>
            </div>

            {/* Tool table */}
            {toolsLoading ? (
              <div className="csp-loading">Loading eligible tools…</div>
            ) : eligibleTools.length === 0 ? (
              <div className="csp-empty-pool">
                No eligible tools at this site. Tools must have approved_scrap = true and a supplier status of YES=SUBMIT, NPN, or CAPACITY to appear here.
              </div>
            ) : (
              <>
                <table className="csp-tool-table">
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox"
                          checked={selected.size === eligibleTools.length && eligibleTools.length > 0}
                          onChange={toggleAll} />
                      </th>
                      <th>Tool ID</th>
                      <th>Alt Tool ID</th>
                      <th>Tool Type</th>
                      <th>Supplier Status</th>
                      <th>Est. Weight (lbs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleTools.map(t => {
                      const tid = t.id || t.tool_id;
                      const isSel = selected.has(tid);
                      return (
                        <tr key={tid} className={isSel ? 'selected' : ''} onClick={() => toggleTool(tid)}>
                          <td><input type="checkbox" checked={isSel} readOnly /></td>
                          <td>{t.supplier_tool_id || '—'}</td>
                          <td>{t.alt_tool_id || '—'}</td>
                          <td>{t.tool_type || '—'}</td>
                          <td>{t.supplier_status || '—'}</td>
                          <td>{t.estimated_weight != null ? Number(t.estimated_weight).toLocaleString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="csp-tool-summary">
                  {selected.size} tool{selected.size !== 1 ? 's' : ''} selected
                  {selected.size > 0 && ` — estimated ${totalWeight.toLocaleString()} lbs total`}
                </div>
              </>
            )}

            {selected.size === 0 && eligibleTools.length > 0 && (
              <div className="csp-hint" style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                Select at least one tool to create the project.
              </div>
            )}

            {createError && <div className="csp-inline-error">{createError}</div>}

            <div className="csp-footer">
              <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                <button className="csp-btn-cancel" onClick={handleCancel}>Cancel</button>
                <button className="csp-btn-secondary" onClick={() => setStep(1)}>&larr; Back</button>
              </div>
              <button className="csp-btn-primary" disabled={selected.size === 0 || creating}
                onClick={handleCreate}>
                {creating ? 'Creating…' : `Create Project (${selected.size} tool${selected.size !== 1 ? 's' : ''})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
